import { State, Selector, Action, StateContext } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { CustomerClient, CustomerContactPersonModel, CustomerModel, UserCustomerModel } from '@core/services/api.service';
import { AddCustomerContactPersonAction, AddCustomerContactPersonFailAction, AddCustomerContactPersonSuccessAction, DeleteCustomerContactPersonAction, DeleteCustomerContactPersonFailAction, DeleteCustomerContactPersonSuccessAction, DeleteEmailAction, DeleteEmailFailAction, DeleteEmailSuccessAction, EditCustomerContactPersonAction, EditCustomerContactPersonFailAction, EditCustomerContactPersonSuccessAction, GetCurrentUserCustomersByUserIdAction, GetCurrentUserCustomersByUserIdFailAction, GetCurrentUserCustomersByUserIdSuccessAction, GetCustomerContactPersonAction, GetCustomerContactPersonFailAction, GetCustomerContactPersonSuccessAction, GetCustomerListAction, GetCustomerListFailAction, GetCustomerListSuccessAction, GetCustomersByUserIdAction, GetCustomersByUserIdFailAction, GetCustomersByUserIdSuccessAction, LinkUserAction, LinkUserFailAction, LinkUserSuccessAction, UpdateCarrierContactEmailAction, UpdateCarrierContactEmailFailAction, UpdateCarrierContactEmailSuccessAction } from './customer.actions';
import { OpenSnackbarAction } from '@ngxs-store/ui-store/ui.actions';
import { SnackbarType } from '@core/models';

export class CustomerStateModel {
  public isLoading: boolean;
  public customers: CustomerModel[];
  public userCustomer: UserCustomerModel;
  public userCustomers: UserCustomerModel[];
  public currentUserCustomers: UserCustomerModel[];
  public customerContacts: CustomerContactPersonModel[];
}

@State<CustomerStateModel>({
  name: 'customers',
  defaults: {
    isLoading: false,
    customers: undefined,
    userCustomer: undefined,
    userCustomers: undefined,
    currentUserCustomers: undefined,
    customerContacts: undefined
  }
})
@Injectable()
export class CustomerState {

  constructor(private customerService: CustomerClient) { }

  @Selector()
  static getCustomerList(state: CustomerStateModel) {
    return state.customers;
  }

  @Selector()
  static getCustomersByUserId(state: CustomerStateModel) {
    return state.userCustomers;
  }
  @Selector()
  static getCurrentUserCustomersByUserId(state: CustomerStateModel) {
    return state.currentUserCustomers;
  }

  @Selector()
  static getCustomerContacts(state: CustomerStateModel) {
    return state.customerContacts;
  }

  @Action(GetCustomerListAction)
  getCustomerListAction({ dispatch, patchState }: StateContext<CustomerStateModel>, action: GetCustomerListAction) {
    patchState({ isLoading: true });

    return this.customerService.getCustomerList().pipe(
      tap((customers) => dispatch(new GetCustomerListSuccessAction({ customers }))),
      catchError((err) => dispatch(new GetCustomerListFailAction()))
    );
  }

  @Action(GetCustomerListSuccessAction)
  getCustomerListSuccessAction(ctx: StateContext<CustomerStateModel>, action: GetCustomerListSuccessAction) {
    ctx.patchState({
      isLoading: false,
      customers: action.payload.customers,
    });
  }

  @Action(GetCustomerListFailAction)
  getCustomerListFailAction(ctx: StateContext<CustomerStateModel>, action: GetCustomerListFailAction) {
    ctx.patchState({
      isLoading: false,
      customers: undefined,
    });
  }

  //----------------------------------------------------------------------------------------------------------

  @Action(LinkUserAction)
  LinkUserAction({ dispatch, patchState }: StateContext<CustomerStateModel>, action: LinkUserAction) {
    patchState({ isLoading: true });

    return this.customerService.linkUserByCustomerNumber(action.userId, action.customerId).pipe(
      tap((userCustomer) => {
        dispatch(new LinkUserSuccessAction({ userCustomer }));
        dispatch(new GetCustomersByUserIdAction(action.userId));
      }),
      catchError(error => {
        dispatch(new LinkUserFailAction());
        throw error;
      })
    );
  }

  @Action(LinkUserSuccessAction)
  assignUserSuccessAction({ patchState, getState }: StateContext<CustomerStateModel>, action: LinkUserSuccessAction) {
    const state = getState();
  }

  //---------------------------------------------------------------------------------------------------

  @Action(GetCustomersByUserIdAction)
  getCustomersByUserIdAction({ dispatch, patchState }: StateContext<CustomerStateModel>, action: GetCustomersByUserIdAction) {
    patchState({ isLoading: true });

    return this.customerService.getCustomersByUserId(action.userId).pipe(
      tap((userCustomers) => dispatch(new GetCustomersByUserIdSuccessAction({ userCustomers }))),
      catchError((err) => dispatch(new GetCustomersByUserIdFailAction()))
    );
  }

  @Action(GetCustomersByUserIdSuccessAction)
  getCustomersByUserIdSuccessAction(ctx: StateContext<CustomerStateModel>, action: GetCustomersByUserIdSuccessAction) {
    ctx.patchState({
      isLoading: false,
      userCustomers: action.payload.userCustomers,
    });
  }

  @Action(GetCustomersByUserIdFailAction)
  getCustomersByUserIdFailAction(ctx: StateContext<CustomerStateModel>, action: GetCustomersByUserIdFailAction) {
    ctx.patchState({
      isLoading: false,
      userCustomers: undefined,
    });
  }

  //---------------------------------------------------------------------------------------------------

  @Action(GetCurrentUserCustomersByUserIdAction)
  getCurrentUserCustomersByUserIdAction({ dispatch, patchState }: StateContext<CustomerStateModel>, action: GetCurrentUserCustomersByUserIdAction) {
    patchState({ isLoading: true });

    return this.customerService.getCustomersByUserId(action.userId).pipe(
      tap((userCustomers) => dispatch(new GetCurrentUserCustomersByUserIdSuccessAction({ userCustomers }))),
      catchError((err) => dispatch(new GetCurrentUserCustomersByUserIdFailAction()))
    );
  }

  @Action(GetCurrentUserCustomersByUserIdSuccessAction)
  getCurrentUserCustomersByUserIdSuccessAction(ctx: StateContext<CustomerStateModel>, action: GetCurrentUserCustomersByUserIdSuccessAction) {
    ctx.patchState({
      isLoading: false,
      currentUserCustomers: action.payload.userCustomers,
    });
  }

  @Action(GetCurrentUserCustomersByUserIdFailAction)
  getCurrentUserCustomersByUserIdFailAction(ctx: StateContext<CustomerStateModel>, action: GetCurrentUserCustomersByUserIdFailAction) {
    ctx.patchState({
      isLoading: false,
      currentUserCustomers: undefined,
    });
  }
  //---------------------------------------------------------------------------------------------

  @Action(AddCustomerContactPersonAction)
  addCustomerContactPersonAction({ dispatch, patchState }: StateContext<CustomerStateModel>, action: AddCustomerContactPersonAction) {
    patchState({ isLoading: true });

    return this.customerService.addCustomerContactPerson(action.regNumber, action.tourId).pipe(
      tap((cust) => {
        return dispatch(new AddCustomerContactPersonSuccessAction({ custContact: cust }));
      }),
      catchError(error => {
        dispatch(new AddCustomerContactPersonFailAction());
        throw error;
      })
    )
  }

  //----------------------------------------------------------------------------------------------------------
  @Action(GetCustomerContactPersonAction)
  getCustomerContactPersonAction({ dispatch, patchState }: StateContext<CustomerStateModel>, action: GetCustomerContactPersonAction) {
    patchState({ isLoading: true });

    return this.customerService.getCustomerContactPersonById().pipe(
      tap((customersContacts) => dispatch(new GetCustomerContactPersonSuccessAction({ customersContacts }))),
      catchError((err) => dispatch(new GetCustomerContactPersonFailAction()))
    );
  }

  @Action(GetCustomerContactPersonSuccessAction)
  getCustomerContactPersonSuccessAction(ctx: StateContext<CustomerStateModel>, action: GetCustomerContactPersonSuccessAction) {
    ctx.patchState({
      isLoading: false,
      customerContacts: action.payload.customersContacts,
    });
  }

  @Action(GetCustomerContactPersonFailAction)
  getCustomerContactPersonFailAction(ctx: StateContext<CustomerStateModel>, action: GetCustomerContactPersonFailAction) {
    ctx.patchState({
      isLoading: false,
      customerContacts: undefined,
    });
  }

  //-------------------------------------------------------------------------------------------------------------------------
  @Action(DeleteCustomerContactPersonAction)
  deleteCustomerContactPersonAction({ dispatch, patchState }: StateContext<CustomerStateModel>, action: DeleteCustomerContactPersonAction) {
    patchState({ isLoading: true });

    return this.customerService.deleteCustomerContactPerson(action.id).pipe(
      tap((cust) => {
        return dispatch(new DeleteCustomerContactPersonSuccessAction({ custContact: cust }));
      }),
      catchError(error => {
        dispatch(new DeleteCustomerContactPersonFailAction());
        throw error;
      })
    )
  }

  @Action(DeleteCustomerContactPersonSuccessAction)
  DeleteCustomerContactPersonSuccessAction(ctx: StateContext<CustomerStateModel>, action: DeleteCustomerContactPersonSuccessAction) {
    ctx.patchState({
      isLoading: false
    });

    ctx.dispatch(new OpenSnackbarAction({ message: 'Emails cleared successfully.', type: SnackbarType.Simple }));
  }

  @Action(DeleteCustomerContactPersonFailAction)
  DeleteCustomerContactPersonFailAction(ctx: StateContext<CustomerStateModel>, action: DeleteCustomerContactPersonFailAction) {
    ctx.patchState({
      isLoading: false
    });

    ctx.dispatch(new OpenSnackbarAction({ message: 'An error occurred while trying to clear emails, please contact admin.', type: SnackbarType.Simple }));
  }

  //----------------------------------------------------------------------------------------------------------

  @Action(EditCustomerContactPersonAction)
  editCustomerContactPersonAction({ dispatch, patchState }: StateContext<CustomerStateModel>, action: EditCustomerContactPersonAction) {
    patchState({ isLoading: true });

    return this.customerService.editCustomerContactById(action.tourId, action.email).pipe(
      tap((cust) => {
        return dispatch(new EditCustomerContactPersonSuccessAction({ tour: cust }));
      }),
      catchError(error => {
        dispatch(new EditCustomerContactPersonFailAction());
        throw error;
      })
    )
  }

  @Action(EditCustomerContactPersonSuccessAction)
  EditCustomerContactPersonSuccessAction(ctx: StateContext<CustomerStateModel>, action: EditCustomerContactPersonSuccessAction) {
    ctx.patchState({
      isLoading: false
    });

    ctx.dispatch(new OpenSnackbarAction({ message: 'Shipment updated successfully.', type: SnackbarType.Simple }));
  }

  @Action(EditCustomerContactPersonFailAction)
  EditCustomerContactPersonFailAction(ctx: StateContext<CustomerStateModel>, action: EditCustomerContactPersonFailAction) {
    ctx.patchState({
      isLoading: false
    });

    ctx.dispatch(new OpenSnackbarAction({ message: 'An error occurred while trying to update shipment, please contact admin.', type: SnackbarType.Simple }));
  }

  //----------------------------------------------------------------------------------------------------------

  @Action(UpdateCarrierContactEmailAction)
  updateCarrierContactEmailAction({ dispatch, patchState }: StateContext<CustomerStateModel>, action: UpdateCarrierContactEmailAction) {
    patchState({ isLoading: true });

    return this.customerService.updateCarrierContactEmail(action.email, action.tourId).pipe(
      tap((tour) => {
        return dispatch(new UpdateCarrierContactEmailSuccessAction({ tour }));
      }),
      catchError(error => {
        dispatch(new UpdateCarrierContactEmailFailAction());
        throw error;
      })
    );
  }

  @Action(UpdateCarrierContactEmailSuccessAction)
  updateCarrierContactEmailSuccessAction(ctx: StateContext<CustomerStateModel>, action: UpdateCarrierContactEmailSuccessAction) {
    ctx.patchState({
      isLoading: false
    });

    ctx.dispatch(new OpenSnackbarAction({ message: 'Carrier contact email updated successfully.', type: SnackbarType.Simple }));
  }

  @Action(UpdateCarrierContactEmailFailAction)
  updateCarrierContactEmailFailAction(ctx: StateContext<CustomerStateModel>, action: UpdateCarrierContactEmailFailAction) {
    ctx.patchState({
      isLoading: false
    });

    ctx.dispatch(new OpenSnackbarAction({ message: 'An error occurred while trying to update carrier contact email, please contact admin.', type: SnackbarType.Simple }));
  }



  //----------------------------------------------------------------------------------------------------------

  @Action(DeleteEmailAction)
  DeleteEmailAction({ dispatch, patchState }: StateContext<CustomerStateModel>, action: DeleteEmailAction) {
    patchState({ isLoading: true });

    return this.customerService.deleteEmailContact(action.id).pipe(
      tap((cust) => {
        return dispatch(new DeleteEmailSuccessAction());
      }),
      catchError(error => {
        dispatch(new DeleteEmailFailAction());
        throw error;
      })
    )
  }


  @Action(DeleteEmailSuccessAction)
  DeleteEmailSuccessAction(ctx: StateContext<CustomerStateModel>, action: DeleteEmailSuccessAction) {
    ctx.patchState({
      isLoading: false
    });

    ctx.dispatch(new OpenSnackbarAction({ message: 'Email deleted successfully.', type: SnackbarType.Simple }));
  }

  @Action(DeleteEmailFailAction)
  DeleteEmailFailAction(ctx: StateContext<CustomerStateModel>, action: DeleteEmailFailAction) {
    ctx.patchState({
      isLoading: false
    });

    ctx.dispatch(new OpenSnackbarAction({ message: 'An error occurred while trying to delete email, please contact admin.', type: SnackbarType.Simple }));
  }


}
